// eTAMS — Overview ("My travel workspace", PDF page 1)
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { etamsAPI } from '../../services/api';
import {
  PageShell, PageHead, Stat, Card, Badge, Empty, Loading, useAsync, money, pad2, fmtDate, fmtRange,
} from '../../components/ui';
import { claimStatus, CATEGORY_GROUP, GROUP_COLORS } from '../../utils/etams';

function Donut({ groups, total }) {
  const size = 170; const stroke = 30; const r = (size - stroke) / 2; const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Expense split">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f6" strokeWidth={stroke} />
      {total > 0 && groups.map((g) => {
        const len = (g.value / total) * c;
        const el = (
          <circle key={g.name} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={GROUP_COLORS[g.name]} strokeWidth={stroke}
            strokeDasharray={`${Math.max(0, len - 2)} ${c - Math.max(0, len - 2)}`} strokeDashoffset={-offset} transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <title>{`${g.name}: ${money(g.value)}`}</title>
          </circle>
        );
        offset += len;
        return el;
      })}
      <text x="50%" y="46%" textAnchor="middle" style={{ fontSize: 19, fontWeight: 700, fill: '#0f2537' }}>{money(total)}</text>
      <text x="50%" y="60%" textAnchor="middle" style={{ fontSize: 13, fill: '#33475b' }}>Total</text>
    </svg>
  );
}

export default function EtamsOverview() {
  const navigate = useNavigate();
  const { data, loading } = useAsync(() => etamsAPI.overview(), []);
  const s = data?.stats || {};
  const trip = data?.nextTrip;

  const groupsMap = {};
  Object.entries(data?.expenseBreakdown?.byCategory || {}).forEach(([cat, v]) => {
    const g = CATEGORY_GROUP[cat] || 'Others';
    groupsMap[g] = (groupsMap[g] || 0) + v;
  });
  const total = data?.expenseBreakdown?.total || 0;
  const groups = ['Travel', 'Hotel', 'Food', 'Local transport', 'Others'].map((name) => ({ name, value: groupsMap[name] || 0 }));

  return (
    <PageShell section="Travel & Expenses">
      <PageHead
        title="My travel workspace"
        subtitle="Manage journeys, claims and reimbursements."
        actions={(
          <>
            <Link to="/etams/claims/new" className="tse-btn lg"><i className="bi bi-file-earmark-plus" />New expense claim</Link>
            <Link to="/etams/travel-requests/new" className="tse-btn lg outline"><i className="bi bi-airplane" />Request travel</Link>
          </>
        )}
      />

      <div className="tse-hero mb-18">
        <i className="bi bi-airplane ic" />
        <div>
          <div className="h">{trip ? 'Ready for your next site visit?' : 'No upcoming journey planned'}</div>
          <div className="s">
            {trip ? <>{trip.origin} → {trip.destination} &nbsp;|&nbsp; {fmtRange(trip.departureDate, trip.returnDate)}{trip.status !== 'APPROVED' ? ' · awaiting approval' : ''}</> : 'Raise a travel request before your next site visit.'}
          </div>
        </div>
        <svg className="mtn" width="240" height="70" viewBox="0 0 240 70" aria-hidden="true"><path d="M0 68 L60 45 L90 55 L130 15 L160 40 L185 10 L240 68" fill="none" stroke="#8fc7ee" strokeWidth="1.5" /></svg>
        <div className="tag d-none d-md-block">Different places<br />Stronger projects<div style={{ width: 33, height: 3, background: '#8fc7ee', marginTop: 6 }} /></div>
      </div>

      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-wallet2" tone="blue" label="Awaiting reimbursement" value={money(s.awaitingReimbursement)} loading={loading} to="/expense/claim-submission" />
        <Stat icon="bi-clock" tone="amber" label="Claims in review" value={pad2(s.claimsInReview)} loading={loading} to="/expense/claim-submission" />
        <Stat icon="bi-check-circle" tone="green" label="Approved travel" value={pad2(s.approvedTravel)} loading={loading} to="/etams/travel-requests" />
        <Stat icon="bi-database" tone="purple" label="Advance requests" value={money(s.advanceRequests)} loading={loading} to="/expense/advance-request" />
      </div>

      <div className="tse-grid ov3 mb-18">
        <Card title="Recent expense reports" actions={<Link className="link" to="/expense/claim-submission">View all</Link>} bodyClass="flush">
          {loading ? <Loading /> : !(data?.recentClaims || []).length ? <Empty icon="bi-file-earmark-text" title="No expense reports yet" action={<Link className="tse-btn sm" to="/etams/claims/new">Create expense claim</Link>} /> : (
            <div className="tse-table-wrap">
              <table className="tse-table nowrap">
                <thead><tr><th>Purpose / Trip</th><th>Report ID</th><th>Amount (₹)</th><th>Status</th><th>Submitted on</th></tr></thead>
                <tbody>
                  {data.recentClaims.slice(0, 4).map((c) => {
                    const st = claimStatus(c);
                    return (
                      <tr key={c._id} className="clickable" onClick={() => navigate('/expense/claim-submission')}>
                        <td className="tse-ellipsis" style={{ maxWidth: 200 }}>{c.title}</td>
                        <td>{c.claimId}</td>
                        <td>{money(c.amount)}</td>
                        <td><Badge tone={st.tone}>{st.label}</Badge></td>
                        <td>{fmtDate(c.submittedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Upcoming journeys" actions={<Link className="link" to="/etams/travel-requests">View all</Link>} bodyClass="flush">
          {loading ? <Loading /> : !(data?.upcomingJourneys || []).length ? <Empty icon="bi-airplane" title="No upcoming journeys" /> : data.upcomingJourneys.slice(0, 3).map((j) => (
            <button type="button" key={j._id} className="tse-list-item" onClick={() => navigate(`/etams/travel-requests?id=${j._id}`)}>
              <span className="ic blue"><i className="bi bi-airplane" /></span>
              <div style={{ minWidth: 0 }}>
                <div className="t">{j.destination}</div>
                <div className="s">{fmtDate(j.departureDate)}</div>
                {j.status !== 'APPROVED' && <div className="m">Awaiting approval</div>}
              </div>
              <i className="bi bi-chevron-right" style={{ marginLeft: 'auto', alignSelf: 'center' }} />
            </button>
          ))}
        </Card>

        <Card title="My expenses">
          <div className="tse-row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <Donut groups={groups} total={total} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {groups.map((g) => (
                <div key={g.name} className="tse-row" style={{ justifyContent: 'space-between', gap: 8, padding: '4px 0', fontSize: '0.791rem', flexWrap: 'nowrap' }}>
                  <span className="tse-row" style={{ gap: 8, flexWrap: 'nowrap' }}><i style={{ width: 12, height: 12, borderRadius: '50%', background: GROUP_COLORS[g.name], display: 'inline-block' }} />{g.name}</span>
                  <strong>{total ? Math.round((g.value / total) * 100) : 0}%</strong>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card title="Quick actions">
        <div className="tse-grid g-3">
          <Link to="/etams/travel-requests/new" className="tse-qa blue">
            <span className="ic" style={{ background: '#d6e8f7', color: '#1479bd' }}><i className="bi bi-airplane" /></span>
            <div><div className="t">Request a journey</div><div className="s">Raise a new travel request for your upcoming visit.</div></div>
            <i className="bi bi-chevron-right chev" />
          </Link>
          <Link to="/etams/receipts" className="tse-qa teal">
            <span className="ic" style={{ background: '#cfeee8', color: '#16857d' }}><i className="bi bi-file-earmark-text" /></span>
            <div><div className="t">Add receipts</div><div className="s">Upload and organise your receipts.</div></div>
            <i className="bi bi-chevron-right chev" />
          </Link>
          <Link to="/etams/policy" className="tse-qa amber">
            <span className="ic" style={{ background: '#fae3bd', color: '#b7791f' }}><i className="bi bi-pie-chart" /></span>
            <div><div className="t">Check entitlements</div><div className="s">View your eligible travel and expense limits.</div></div>
            <i className="bi bi-chevron-right chev" />
          </Link>
        </div>
      </Card>
    </PageShell>
  );
}
